﻿import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, Play, Pause, Highlighter, Share2, Sparkles, Check, Clock,
  Headphones, BookOpen, ArrowRight, Bookmark, MoreHorizontal, User,
  Briefcase, Home, DollarSign, Heart, Compass, Zap,
} from 'lucide-react';

// ============================================================
// STACK — Weekly Book Intelligence Briefing
// Demo: Week 4 for Mike Coffey (Type 3w4, building Leverage + Golden Fox)
// ============================================================

// ---------------- Design tokens (light palette) ----------------
const PAPER = '#FBF7EC';        // warm cream — main canvas
const PAPER_2 = '#F2ECDA';      // soft surface for cards
const PAPER_3 = '#FFFFFF';      // pure surface for emphasis
const INK = '#1F1A14';          // soft near-black, warm
const INK_MID = '#5B5347';
const INK_SOFT = '#A39A85';
const LINE = '#E8DFC8';
const ACCENT = '#D97757';       // soft coral — the new brand signal
const ACCENT_DEEP = '#B45A40';
const ACCENT_LITE = '#F4A88E';
const SIGNAL = '#7E9CA8';       // muted editorial teal
const SAGE = '#9CAF88';         // soft sage
const HIGHLIGHT = '#FAEEC6';    // pale wheat for highlight callouts
const FRAME_TOP = '#D4C8AB';    // warm sand gradient (around phone)
const FRAME_BOT = '#A89A78';

// ---------------- Fonts ----------------
function FontStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600;9..144,700;9..144,800&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
      .ff-display { font-family: 'Fraunces', Georgia, serif; font-feature-settings: "ss01" on, "ss02" on; }
      .ff-ui      { font-family: 'Inter', system-ui, sans-serif; }
      .ff-mono    { font-family: 'JetBrains Mono', ui-monospace, monospace; }
      .smallcaps  { font-family: 'Inter', sans-serif; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; font-size: 10px; }
      .grain {
        background-image:
          radial-gradient(rgba(20,17,13,0.025) 1px, transparent 1px),
          radial-gradient(rgba(20,17,13,0.012) 1px, transparent 1px);
        background-size: 4px 4px, 8px 8px;
        background-position: 0 0, 2px 2px;
      }
      .scrollbar-hide::-webkit-scrollbar { display: none; }
      .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
    `}</style>
  );
}

// ---------------- Brand mark ----------------
function StackMark({ size = 28, color = INK }) {
  // Three nested rectangles forming a stack of book spines
  return (
    <svg width={size} height={size * 0.85} viewBox="0 0 32 28" fill="none">
      <rect x="2" y="2"  width="28" height="6"  rx="1.5" stroke={color} strokeWidth="1.6" />
      <rect x="4" y="11" width="24" height="6"  rx="1.5" fill={color} />
      <rect x="2" y="20" width="28" height="6"  rx="1.5" stroke={color} strokeWidth="1.6" />
    </svg>
  );
}

// ---------------- Book covers ----------------
// Stylized SVG approximations — not real cover art, but recognizable.

function CoverEmpowered({ w = 200 }) {
  const h = w * 1.45;
  return (
    <svg width={w} height={h} viewBox="0 0 200 290" style={{ display: 'block' }}>
      <rect width="200" height="290" fill="#B91C1C" />
      <rect x="14" y="14" width="172" height="262" fill="none" stroke="#FCA5A5" strokeWidth="1" />
      <text x="100" y="60" textAnchor="middle" fill="#FECACA" fontFamily="Inter" fontSize="9" fontWeight="600" letterSpacing="2">MARTY CAGAN</text>
      <text x="100" y="148" textAnchor="middle" fill="#FFFBEB" fontFamily="Fraunces" fontSize="38" fontWeight="700" letterSpacing="-1">EM</text>
      <text x="100" y="184" textAnchor="middle" fill="#FFFBEB" fontFamily="Fraunces" fontSize="38" fontWeight="700" letterSpacing="-1">POW</text>
      <text x="100" y="220" textAnchor="middle" fill="#FFFBEB" fontFamily="Fraunces" fontSize="38" fontWeight="700" letterSpacing="-1">ERED</text>
      <text x="100" y="262" textAnchor="middle" fill="#FECACA" fontFamily="Inter" fontSize="7" letterSpacing="1.6">ORDINARY PEOPLE • EXTRAORDINARY PRODUCTS</text>
    </svg>
  );
}

function CoverInfiniteGame({ w = 200 }) {
  const h = w * 1.45;
  return (
    <svg width={w} height={h} viewBox="0 0 200 290">
      <rect width="200" height="290" fill="#1E3A8A" />
      <circle cx="100" cy="120" r="48" fill="none" stroke="#FBBF24" strokeWidth="3" />
      <circle cx="100" cy="120" r="48" fill="none" stroke="#FBBF24" strokeWidth="3" transform="rotate(60 100 120)" strokeDasharray="6 4" opacity="0.6" />
      <text x="100" y="200" textAnchor="middle" fill="#FBBF24" fontFamily="Fraunces" fontSize="20" fontWeight="700">THE INFINITE</text>
      <text x="100" y="226" textAnchor="middle" fill="#FFFFFF" fontFamily="Fraunces" fontSize="28" fontWeight="800" letterSpacing="-0.5">GAME</text>
      <text x="100" y="262" textAnchor="middle" fill="#FBBF24" fontFamily="Inter" fontSize="9" fontWeight="600" letterSpacing="2">SIMON SINEK</text>
    </svg>
  );
}

function CoverDieWithZero({ w = 200 }) {
  const h = w * 1.45;
  return (
    <svg width={w} height={h} viewBox="0 0 200 290">
      <rect width="200" height="290" fill="#FACC15" />
      <text x="100" y="80" textAnchor="middle" fill="#1F2937" fontFamily="Inter" fontSize="9" fontWeight="700" letterSpacing="2">BILL PERKINS</text>
      <text x="100" y="136" textAnchor="middle" fill="#1F2937" fontFamily="Fraunces" fontSize="36" fontWeight="800" letterSpacing="-1">DIE</text>
      <text x="100" y="172" textAnchor="middle" fill="#1F2937" fontFamily="Fraunces" fontSize="22" fontWeight="500" fontStyle="italic">with</text>
      <text x="100" y="222" textAnchor="middle" fill="#1F2937" fontFamily="Fraunces" fontSize="50" fontWeight="800" letterSpacing="-2">ZERO</text>
      <text x="100" y="262" textAnchor="middle" fill="#1F2937" fontFamily="Inter" fontSize="7" letterSpacing="1.6">GETTING ALL YOU CAN FROM YOUR MONEY AND YOUR LIFE</text>
    </svg>
  );
}

function CoverBoundaries({ w = 200 }) {
  const h = w * 1.45;
  return (
    <svg width={w} height={h} viewBox="0 0 200 290">
      <defs>
        <linearGradient id="bgrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#A7F3D0"/>
          <stop offset="100%" stopColor="#6EE7B7"/>
        </linearGradient>
      </defs>
      <rect width="200" height="290" fill="url(#bgrad)" />
      <rect x="22" y="22" width="156" height="246" fill="none" stroke="#065F46" strokeWidth="1.2" />
      <text x="100" y="68" textAnchor="middle" fill="#064E3B" fontFamily="Inter" fontSize="8" fontWeight="600" letterSpacing="2.4">UPDATED & EXPANDED</text>
      <text x="100" y="142" textAnchor="middle" fill="#064E3B" fontFamily="Fraunces" fontSize="40" fontWeight="700" letterSpacing="-1">Boundaries</text>
      <line x1="60" y1="156" x2="140" y2="156" stroke="#065F46" strokeWidth="1" />
      <text x="100" y="178" textAnchor="middle" fill="#065F46" fontFamily="Fraunces" fontStyle="italic" fontSize="11">when to say yes</text>
      <text x="100" y="194" textAnchor="middle" fill="#065F46" fontFamily="Fraunces" fontStyle="italic" fontSize="11">how to say no</text>
      <text x="100" y="252" textAnchor="middle" fill="#064E3B" fontFamily="Inter" fontSize="9" fontWeight="600" letterSpacing="1.8">DR. HENRY CLOUD</text>
      <text x="100" y="266" textAnchor="middle" fill="#064E3B" fontFamily="Inter" fontSize="9" fontWeight="600" letterSpacing="1.8">DR. JOHN TOWNSEND</text>
    </svg>
  );
}

function CoverAntifragile({ w = 200 }) {
  const h = w * 1.45;
  return (
    <svg width={w} height={h} viewBox="0 0 200 290">
      <rect width="200" height="290" fill="#FAFAFA" />
      <rect x="0" y="0" width="200" height="14" fill="#000" />
      <rect x="0" y="276" width="200" height="14" fill="#000" />
      <text x="100" y="86" textAnchor="middle" fill="#000" fontFamily="Fraunces" fontSize="32" fontWeight="800" letterSpacing="-1">ANTI</text>
      <text x="100" y="124" textAnchor="middle" fill="#DC2626" fontFamily="Fraunces" fontSize="32" fontWeight="800" letterSpacing="-1">FRAGILE</text>
      <line x1="50" y1="138" x2="150" y2="138" stroke="#000" strokeWidth="0.8" />
      <text x="100" y="166" textAnchor="middle" fill="#000" fontFamily="Fraunces" fontSize="11" fontStyle="italic">Things That Gain</text>
      <text x="100" y="182" textAnchor="middle" fill="#000" fontFamily="Fraunces" fontSize="11" fontStyle="italic">From Disorder</text>
      <text x="100" y="232" textAnchor="middle" fill="#000" fontFamily="Inter" fontSize="9" fontWeight="700" letterSpacing="2">NASSIM NICHOLAS</text>
      <text x="100" y="246" textAnchor="middle" fill="#000" fontFamily="Inter" fontSize="9" fontWeight="700" letterSpacing="2">TALEB</text>
    </svg>
  );
}

const COVER_BY_ID = {
  empowered:    CoverEmpowered,
  infinite:     CoverInfiniteGame,
  diewithzero:  CoverDieWithZero,
  boundaries:   CoverBoundaries,
  antifragile:  CoverAntifragile,
};

function BookCover({ id, w = 200 }) {
  const Component = COVER_BY_ID[id];
  if (!Component) return null;
  return (
    <div style={{ boxShadow: '0 18px 36px rgba(20,17,13,0.18), 0 4px 10px rgba(20,17,13,0.10)', borderRadius: 2, overflow: 'hidden' }}>
      <Component w={w} />
    </div>
  );
}

// ---------------- Data ----------------
const FOCUS_AREAS = [
  'Building a business', 'AI fluency', 'Finance & investing',
  'Career reinvention', 'Job search', 'Big decisions',
  'Leadership', 'Strategy & ops', 'Time & attention',
  'Marriage', 'Parenting', 'Blended families',
  'Divorce & rebuilding', 'Faith', 'Mental health',
  'Health & longevity', 'Friendship', 'Legacy & money',
  'Writing & creativity', 'Philosophy', 'History & geopolitics',
  'Tech & society', 'Personal growth', 'Productivity',
];

const PRESELECTED = new Set([
  'Building a business', 'AI fluency', 'Leadership',
  'Career reinvention', 'Big decisions', 'Legacy & money',
  'Strategy & ops',
]);

const ENNEAGRAM_QUESTIONS = [
  "I would rather be admired for the result than understood for the effort.",
  "I move fastest when I have a clear scoreboard and a clear opponent.",
  "There is a version of me I show the world, and a version of me I don't.",
];

// Each book is paired to ONE of the user's 5 focuses for the week.
// Mike's 5 focuses this week: Building a business, Leadership, Legacy & money, Marriage, Big decisions.
const BOOKS = [
  {
    id: 'empowered', day: 1, dow: 'Mon', date: 'Apr 27',
    author: 'Marty Cagan',
    title: 'Empowered',
    subtitle: 'Ordinary People, Extraordinary Products',
    year: 2020,
    category: 'Product Management',
    focus: 'Building a business',
    minutes: 15,
    accent: '#B91C1C',
    status: 'complete',
    headline: 'Why feature factories survive — and how to build the alternative.',
  },
  {
    id: 'infinite', day: 2, dow: 'Tue', date: 'Apr 28',
    author: 'Simon Sinek',
    title: 'The Infinite Game',
    subtitle: 'Lead like you intend to keep playing.',
    year: 2019,
    category: 'Leadership',
    focus: 'Leadership',
    minutes: 15,
    accent: '#1E3A8A',
    status: 'complete',
    headline: 'The leaders who endure are playing a fundamentally different game.',
  },
  {
    id: 'diewithzero', day: 3, dow: 'Wed', date: 'Apr 29',
    author: 'Bill Perkins',
    title: 'Die With Zero',
    subtitle: 'Spend your money on the right experiences at the right time.',
    year: 2020,
    category: 'Money & Life',
    focus: 'Legacy & money',
    minutes: 16,
    accent: '#FACC15',
    status: 'today',
    headline: 'Optimizing net worth at death is the wrong objective function.',
  },
  {
    id: 'boundaries', day: 4, dow: 'Thu', date: 'Apr 30',
    author: 'Cloud & Townsend',
    title: 'Boundaries',
    subtitle: 'When to say yes, how to say no.',
    year: 1992,
    category: 'Relationships & Self',
    focus: 'Marriage',
    minutes: 14,
    accent: '#10B981',
    status: 'upcoming',
    headline: "What you tolerate, you teach. Boundaries are the architecture of love.",
  },
  {
    id: 'antifragile', day: 5, dow: 'Fri', date: 'May 1',
    author: 'Nassim Taleb',
    title: 'Antifragile',
    subtitle: 'Things that gain from disorder.',
    year: 2012,
    category: 'Risk & Uncertainty',
    focus: 'Big decisions',
    minutes: 16,
    accent: '#DC2626',
    status: 'upcoming',
    headline: 'Stop trying to predict shocks. Build systems that get stronger from them.',
  },
];

const WEEK_FOCUSES = ['Building a business', 'Leadership', 'Legacy & money', 'Marriage', 'Big decisions'];

// Featured (today's) book — full briefing content
const FEATURED = {
  ...BOOKS[2],
  oneSentence:
    "Optimizing your life around your net worth at death is the wrong objective function — the right one is maximizing the experiences your time, money, and health can buy together, on the curve where each is still abundant.",
  whyNow:
    "You're 41, building two companies, and you're already starting to back-load your life — telling yourself you'll travel more, spend more time with the kids, slow down once Leverage stabilizes. Perkins is calling that bluff. The version of you that finally relaxes at 65 is not the same person who could have run that ridge in Patagonia at 41 with the boys. The compounding logic that built your wealth is the same logic that's costing you the experiences only this decade can give you.",
  bigIdeas: [
    {
      h: 'Memory dividends',
      b: "Every great experience pays a dividend forever — every time you remember it, retell it, draw on it. Perkins's argument is that the value of an experience is not just the moment, it's the integral of every memory of it, multiplied by every year you have left to remember it. Which means the same trip taken at 35 is worth more than at 65, not because your body is better but because you have more years of memory dividend ahead.",
    },
    {
      h: 'The three currencies',
      b: "Money, time, and health are the three currencies of experience — and only one of them compounds in your favor. Money compounds. Time and health both run down. The mistake most successful people make is over-saving the currency that compounds while burning through the two that don't.",
    },
    {
      h: 'Time-bucket your life',
      b: "Don't ask 'what's on my bucket list?' — ask 'in which decade can each thing on my bucket list still actually happen?' Backpacking through Patagonia: 30s and 40s. Slow walks with grandkids: 60s and 70s. Treating life as a single bucket means putting things off into a decade where they're no longer possible.",
    },
    {
      h: "Give it while you're alive",
      b: "If you plan to give your kids money, the worst possible time to give it is at your death — they'll be in their 50s or 60s, past the point where the money would have most changed their trajectory. The right window to transfer wealth is when it can compound in their lives, typically their late 20s through 40s. Same logic for charity.",
    },
    {
      h: 'The peak utility curve',
      b: "Money has declining marginal utility as you accumulate it; experience capacity has declining marginal utility as you age. The two curves cross. Beyond that crossover, every additional dollar saved costs you more in foregone experience than it ever produces in extra security. Most high-earners have crossed it without noticing.",
    },
  ],
  frameworks: [
    "Calculate your peak net worth date — the year your net worth should peak, after which you deliberately spend it down. For most people who saved aggressively in their 30s, this is in their 50s, not at death.",
    "Run the time-bucket exercise — sort every aspiration onto the decade in which it can still happen. Anything in the next decade-bucket gets calendared this year.",
    "Lock the kids' inheritance window — pick a target year (typical: when oldest is 30) and start the transfer plan now, not in your will.",
  ],
  stories: [
    {
      h: "Perkins's Caribbean party for his mom",
      b: "Perkins flew his elderly mother and her closest friends to a private Caribbean reunion. It cost a meaningful slice of his net worth and she died less than two years later. He frames it as the highest-yielding investment he's ever made — the memories, for him and for her, paid out a return that no public market could replicate.",
    },
    {
      h: "The trader who saved his way out of his prime",
      b: "Perkins describes a colleague who saved every spare dollar in his 30s and 40s expecting to retire rich at 50. He hit 50 with the number — and discovered his health had quietly degraded to where the trip he'd saved for was no longer something he could physically do. The savings worked. The plan didn't.",
    },
  ],
  contrarian:
    "The criticism — and it's fair — is that Perkins is writing from the position of an already-wealthy hedge fund manager whose downside risk is asymmetrically smaller than the median reader's. Telling someone with $2M to spend it down is reasonable; telling someone with $200K the same thing in a country with no real safety net is irresponsible. The framework still applies, but the safety margins matter and Perkins underweights them.",
  takeaways: [
    {
      t: "Net worth at death is a spreadsheet error, not a life goal.",
      a: "Pick a peak net worth year. After it, your spending should exceed your savings on purpose.",
    },
    {
      t: "Experiences with your kids while they're young are time-locked assets.",
      a: "The trip with the boys at 8 and 11 cannot be re-bought at 12 and 15. Calendar them this quarter.",
    },
    {
      t: "If you intend to give the kids money, the worst time is at your death.",
      a: "Decide their inheritance window now (target: when they're 28–32). Start the transfer plan.",
    },
  ],
  questions: [
    "Which of your current savings goals are actually about security — and which are habit dressed up as security?",
    "If your peak net worth year is supposed to be 12 years from now, what does this year's spending plan need to look like?",
    "What's the experience your kids could only have with you in the next three years?",
  ],
  sixty:
    "Perkins's argument lands hardest on people who built wealth through discipline: the same discipline that compounds your portfolio is, past a point, costing you the experiences only this season of life can buy. Optimize for memory dividends, not net worth at death. Bucket your aspirations by decade. Give the kids the money while it can still change their lives. Pick your peak net worth year and start the descent on purpose.",
};

// ---------------- UI primitives ----------------
function StatusBar({ light = false }) {
  const color = light ? 'rgba(255,255,255,0.85)' : INK;
  return (
    <div className="flex justify-between items-center px-7 pt-3 pb-1 ff-mono" style={{ fontSize: 13, color, fontWeight: 600 }}>
      <span>9:41</span>
      <div className="flex items-center gap-1.5" style={{ opacity: 0.95 }}>
        <span style={{ fontSize: 12 }}>•••</span>
        <svg width="16" height="11" viewBox="0 0 16 11"><path d="M8 1 L1 4.5 L8 8 L15 4.5 Z" stroke={color} strokeWidth="1.2" fill="none" /></svg>
        <svg width="22" height="11" viewBox="0 0 22 11"><rect x="0.5" y="1" width="18" height="9" rx="2" stroke={color} strokeWidth="1" fill="none" /><rect x="2" y="2.5" width="14" height="6" rx="0.5" fill={color} /><rect x="20" y="3.5" width="1.5" height="4" rx="0.5" fill={color} /></svg>
      </div>
    </div>
  );
}

function PhoneFrame({ children }) {
  return (
    <div style={{ width: 412, height: 850, borderRadius: 56, padding: 6, background: 'linear-gradient(160deg, #4a4338 0%, #2a241d 100%)', boxShadow: '0 40px 80px -25px rgba(60,45,25,0.35), 0 20px 40px -25px rgba(60,45,25,0.22), inset 0 0 1px rgba(255,255,255,0.12)' }}>
      <div style={{ width: '100%', height: '100%', borderRadius: 50, overflow: 'hidden', position: 'relative', background: PAPER }}>
        <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', width: 110, height: 28, background: '#1a1612', borderRadius: 14, zIndex: 50 }} />
        {children}
      </div>
    </div>
  );
}

function HomeIndicator() {
  return (
    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 rounded-full" style={{ background: INK, opacity: 0.32 }} />
  );
}

// ============================================================
// SCREEN 1 — Welcome
// ============================================================
function WelcomeScreen({ onNext }) {
  return (
    <div className="w-full h-full flex flex-col grain" style={{ background: `linear-gradient(180deg, ${PAPER_3} 0%, ${PAPER} 45%, ${PAPER_2} 100%)`, color: INK }}>
      <StatusBar />

      {/* Tilted stack of book covers as hero */}
      <div className="relative" style={{ height: 380, marginTop: 36 }}>
        <div style={{ position: 'absolute', left: '50%', top: 30, transform: 'translateX(-62%) rotate(-9deg)' }}>
          <BookCover id="boundaries" w={148} />
        </div>
        <div style={{ position: 'absolute', left: '50%', top: 18, transform: 'translateX(-18%) rotate(8deg)' }}>
          <BookCover id="antifragile" w={148} />
        </div>
        <div style={{ position: 'absolute', left: '50%', top: 52, transform: 'translateX(-50%)' }}>
          <BookCover id="diewithzero" w={172} />
        </div>
      </div>

      <div className="px-8 mt-2 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-7">
          <StackMark size={22} color={INK} />
          <span className="ff-ui smallcaps" style={{ color: INK_MID }}>Stack</span>
        </div>

        <h1 className="ff-display" style={{ fontSize: 40, lineHeight: 1.02, fontWeight: 600, letterSpacing: -1.2, color: INK }}>
          Five books<br/>
          a week.<br/>
          <span style={{ fontStyle: 'italic', fontWeight: 400, color: ACCENT }}>Briefed for you.</span>
        </h1>

        <p className="ff-display mt-5" style={{ fontSize: 16, lineHeight: 1.55, color: INK_MID, maxWidth: 320 }}>
          Not summaries. A weekly intelligence briefing on five books — picked for your Enneagram, your stage of life, and the five things you're actually carrying right now.
        </p>

        <div className="mt-auto pb-2">
          <button onClick={onNext} className="w-full py-4 rounded-full ff-ui font-medium flex items-center justify-center gap-2 transition active:scale-[0.99]" style={{ background: ACCENT, color: PAPER, fontSize: 15, boxShadow: '0 8px 22px -8px rgba(217,119,87,0.55)' }}>
            Begin <ArrowRight size={16} strokeWidth={2} />
          </button>
          <div className="text-center mt-4 ff-ui" style={{ fontSize: 12, color: INK_SOFT }}>
            7 days free. Then $14.99/mo or $129/yr.
          </div>
        </div>
      </div>
      <HomeIndicator />
    </div>
  );
}

// ============================================================
// SCREEN 2 — Onboarding (Enneagram)
// ============================================================
function OnboardingScreen({ onNext, onBack }) {
  const [qIdx, setQIdx] = useState(0);
  const [answer, setAnswer] = useState(null);

  const handleAnswer = (val) => {
    setAnswer(val);
    setTimeout(() => {
      if (qIdx < ENNEAGRAM_QUESTIONS.length - 1) {
        setQIdx(qIdx + 1);
        setAnswer(null);
      } else {
        onNext();
      }
    }, 320);
  };

  const totalQ = 36;
  const currentQ = qIdx + 5;
  const pct = (currentQ / totalQ) * 100;

  return (
    <div className="w-full h-full flex flex-col grain" style={{ background: PAPER, color: INK }}>
      <StatusBar />
      <div className="px-7 pt-3 flex items-center justify-between">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full transition active:scale-95" style={{ color: INK_MID }}>
          <ChevronLeft size={20} />
        </button>
        <div className="ff-mono" style={{ fontSize: 11, color: INK_SOFT, letterSpacing: 1.4 }}>
          {String(currentQ).padStart(2, '0')} / {totalQ}
        </div>
        <div style={{ width: 24 }} />
      </div>

      {/* progress */}
      <div className="px-7 mt-2">
        <div className="h-[2px] rounded-full" style={{ background: LINE }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: ACCENT }} />
        </div>
      </div>

      <div className="flex-1 flex flex-col px-7 pt-12">
        <div className="smallcaps" style={{ color: ACCENT, marginBottom: 14 }}>How you read the world</div>
        <div className="ff-display" style={{ fontSize: 26, lineHeight: 1.22, fontWeight: 500, color: INK, letterSpacing: -0.4 }}>
          {ENNEAGRAM_QUESTIONS[qIdx]}
        </div>

        <div className="mt-auto pb-4">
          <div className="flex justify-between mb-3 ff-ui" style={{ fontSize: 11, color: INK_SOFT }}>
            <span>Disagree</span>
            <span>Neutral</span>
            <span>Agree</span>
          </div>
          <div className="flex items-center justify-between">
            {[1,2,3,4,5].map((v, i) => {
              const active = answer === v;
              const sizes = [42, 48, 38, 48, 42];
              return (
                <button key={v} onClick={() => handleAnswer(v)} className="rounded-full transition-all active:scale-90" style={{ width: sizes[i], height: sizes[i], background: active ? ACCENT : 'transparent', border: `1.5px solid ${active ? ACCENT : INK_SOFT}` }} />
              );
            })}
          </div>
          <div className="text-center mt-7 ff-ui italic" style={{ fontSize: 12, color: INK_SOFT, lineHeight: 1.5 }}>
            36 questions across the 9 types.<br />
            We use this to point your weekly stack at what you actually need to hear.
          </div>
        </div>
      </div>
      <HomeIndicator />
    </div>
  );
}

// ============================================================
// SCREEN 3 — Focus areas
// ============================================================
function FocusAreasScreen({ onNext, onBack }) {
  const [selected, setSelected] = useState(new Set(PRESELECTED));
  const toggle = (area) => {
    const next = new Set(selected);
    if (next.has(area)) next.delete(area); else next.add(area);
    setSelected(next);
  };
  const count = selected.size;
  const enabled = count >= 5;

  return (
    <div className="w-full h-full flex flex-col grain" style={{ background: PAPER, color: INK }}>
      <StatusBar />
      <div className="px-7 pt-3 flex items-center justify-between">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full transition active:scale-95" style={{ color: INK_MID }}>
          <ChevronLeft size={20} />
        </button>
        <div className="smallcaps" style={{ color: INK_SOFT }}>Focus</div>
        <div style={{ width: 24 }} />
      </div>

      <div className="px-7 mt-6 flex-1 flex flex-col">
        <div className="ff-display" style={{ fontSize: 30, lineHeight: 1.05, fontWeight: 600, color: INK, letterSpacing: -0.6 }}>
          What are you<br/>carrying right now?
        </div>
        <p className="ff-ui mt-3" style={{ fontSize: 13.5, color: INK_MID, lineHeight: 1.55 }}>
          Pick at least five. Your weekly stack will pull from the books that actually move on these.
        </p>

        <div className="mt-5 -mx-1 flex flex-wrap gap-2 overflow-y-auto scrollbar-hide" style={{ maxHeight: 460 }}>
          {FOCUS_AREAS.map((area) => {
            const isSelected = selected.has(area);
            return (
              <button key={area} onClick={() => toggle(area)} className="px-3.5 py-2 rounded-full ff-ui transition-all active:scale-95" style={{ fontSize: 12.5, background: isSelected ? INK : 'transparent', color: isSelected ? PAPER : INK, border: `1px solid ${isSelected ? INK : LINE}`, fontWeight: isSelected ? 500 : 400 }}>
                {area}
              </button>
            );
          })}
        </div>

        <div className="mt-auto pb-2">
          <div className="flex items-center justify-between ff-ui mb-3" style={{ fontSize: 12 }}>
            <span style={{ color: INK_SOFT }}>{count} selected</span>
            <span style={{ color: enabled ? ACCENT : INK_SOFT }}>{enabled ? "You're ready" : `Pick ${5 - count} more`}</span>
          </div>
          <button onClick={onNext} disabled={!enabled} className="w-full py-4 rounded-full ff-ui font-medium flex items-center justify-center gap-2 transition active:scale-[0.99]" style={{ background: enabled ? ACCENT : LINE, color: enabled ? '#FFFBEB' : INK_SOFT, fontSize: 15 }}>
            Build my first stack <ArrowRight size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
      <HomeIndicator />
    </div>
  );
}

// ============================================================
// SCREEN 4 — Weekly plan (5 books, Mon–Fri)
// ============================================================
function WeeklyPlanScreen({ onNext, onBack }) {
  return (
    <div className="w-full h-full flex flex-col" style={{ background: PAPER, color: INK }}>
      <StatusBar />

      <div className="px-7 pt-3 flex items-center justify-between">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full transition active:scale-95" style={{ color: INK_MID }}>
          <ChevronLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          <StackMark size={18} />
          <span className="smallcaps" style={{ color: INK_SOFT }}>Stack</span>
        </div>
        <button className="p-2 -mr-2 rounded-full transition active:scale-95" style={{ color: INK_MID }}>
          <User size={18} strokeWidth={1.7} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide pb-32">
        {/* Header strip */}
        <div className="px-7 mt-4">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="smallcaps" style={{ color: ACCENT }}>Week 4 — Apr 27</div>
              <div className="ff-display mt-1.5" style={{ fontSize: 30, lineHeight: 1.05, fontWeight: 600, letterSpacing: -0.6, color: INK }}>
                Good morning,<br />Mike.
              </div>
            </div>
          </div>
          <p className="ff-display mt-4" style={{ fontSize: 15, lineHeight: 1.5, color: INK_MID, fontStyle: 'italic' }}>
            Five books this week, paced for the operator you're becoming. <span style={{ color: INK }}>~75 minutes total.</span>
          </p>
        </div>

        {/* This week theme card */}
        <div className="mx-7 mt-6 p-5 rounded-2xl" style={{ background: PAPER_3, border: `1px solid ${LINE}`, boxShadow: '0 1px 0 rgba(31,26,20,0.02)' }}>
          <div className="smallcaps" style={{ color: ACCENT, marginBottom: 10 }}>Your five focuses this week</div>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {WEEK_FOCUSES.map((f) => (
              <span key={f} className="ff-ui px-2.5 py-1 rounded-full" style={{ fontSize: 11, background: PAPER_2, color: INK, border: `1px solid ${LINE}` }}>
                {f}
              </span>
            ))}
          </div>
          <div className="ff-display" style={{ fontSize: 16, lineHeight: 1.45, fontWeight: 500, color: INK_MID, fontStyle: 'italic' }}>
            One book paired to each focus — chosen for a Type 3w4 building a business while raising a blended family.
          </div>
          <div className="mt-3 flex items-center gap-1.5 ff-mono" style={{ fontSize: 10.5, color: INK_SOFT, letterSpacing: 1.2 }}>
            <Clock size={11} /> 75 MIN · 5 BOOKS · 5 FOCUSES
          </div>
        </div>

        {/* Books list */}
        <div className="px-7 mt-7">
          <div className="smallcaps mb-4" style={{ color: INK_SOFT }}>The five</div>

          {BOOKS.map((b, idx) => {
            const isToday = b.status === 'today';
            const isDone = b.status === 'complete';
            const isLocked = b.status === 'upcoming';
            return (
              <button
                key={b.id}
                onClick={isToday ? onNext : undefined}
                className="w-full mb-3.5 p-4 rounded-2xl flex items-stretch gap-4 transition active:scale-[0.995] text-left"
                style={{
                  background: isToday ? PAPER_3 : 'transparent',
                  border: `1px solid ${isToday ? ACCENT : LINE}`,
                  opacity: isLocked ? 0.62 : 1,
                  boxShadow: isToday ? '0 8px 24px -10px rgba(217,119,87,0.30)' : 'none',
                  cursor: isToday ? 'pointer' : 'default',
                }}
              >
                <div style={{ flexShrink: 0 }}>
                  <BookCover id={b.id} w={70} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <div className="ff-mono smallcaps" style={{ color: isToday ? ACCENT : INK_SOFT }}>
                      {b.dow.toUpperCase()} · {b.date}
                    </div>
                    {isDone && (
                      <div className="flex items-center gap-1 ff-mono" style={{ fontSize: 9, color: '#5C8A5A', letterSpacing: 1 }}>
                        <Check size={10} strokeWidth={2.5} /> DONE
                      </div>
                    )}
                    {isToday && (
                      <div className="px-1.5 py-0.5 rounded ff-mono" style={{ fontSize: 9, color: PAPER, background: ACCENT, letterSpacing: 1 }}>
                        TODAY
                      </div>
                    )}
                    <span className="ff-ui px-2 py-0.5 rounded-full" style={{ fontSize: 10, background: PAPER_2, color: INK_MID, border: `1px solid ${LINE}` }}>
                      {b.focus}
                    </span>
                  </div>
                  <div className="ff-display" style={{ fontSize: 17, lineHeight: 1.18, fontWeight: 600, color: INK, letterSpacing: -0.3 }}>
                    {b.title}
                  </div>
                  <div className="ff-ui mt-0.5" style={{ fontSize: 11.5, color: INK_SOFT }}>
                    {b.author} · {b.year}
                  </div>
                  <div className="ff-display mt-2" style={{ fontSize: 12.5, color: INK_MID, lineHeight: 1.4, fontStyle: 'italic' }}>
                    {b.headline}
                  </div>
                  <div className="flex items-center gap-2 mt-2 ff-mono" style={{ fontSize: 10, color: INK_SOFT, letterSpacing: 0.8 }}>
                    <span className="flex items-center gap-1"><Clock size={10} /> {b.minutes} MIN</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Floating CTA */}
      <div className="absolute bottom-0 left-0 right-0 px-7 pb-6 pt-3" style={{ background: `linear-gradient(to top, ${PAPER} 65%, rgba(248,245,238,0))` }}>
        <button onClick={onNext} className="w-full py-4 rounded-full ff-ui font-medium flex items-center justify-center gap-2 transition active:scale-[0.99]" style={{ background: INK, color: '#FFFBEB', fontSize: 14.5 }}>
          <Headphones size={16} strokeWidth={1.8} /> Start today's briefing
        </button>
      </div>
      <HomeIndicator />
    </div>
  );
}

// ============================================================
// SCREEN 5 — Book detail (today's briefing)
// ============================================================
function BookDetailScreen({ onBack }) {
  const [progress, setProgress] = useState(28);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [tab, setTab] = useState('argument');

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setProgress((p) => Math.min(100, p + 0.35)), 250);
    return () => clearInterval(id);
  }, [playing]);

  const totalSec = FEATURED.minutes * 60;
  const currentSec = Math.floor((progress / 100) * totalSec);
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const cycleSpeed = () => {
    const next = { 1: 1.25, 1.25: 1.5, 1.5: 0.75, 0.75: 1 }[speed] || 1;
    setSpeed(next);
  };

  const TABS = [
    { id: 'argument',    label: 'Argument' },
    { id: 'ideas',       label: 'Big Ideas' },
    { id: 'frameworks',  label: 'Frameworks' },
    { id: 'stories',     label: 'Stories' },
    { id: 'takeaways',   label: 'Takeaways' },
    { id: 'questions',   label: 'Questions' },
  ];

  return (
    <div className="w-full h-full flex flex-col" style={{ background: PAPER, color: INK }}>
      <StatusBar />

      {/* Top bar */}
      <div className="px-7 pt-3 flex items-center justify-between">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full transition active:scale-95" style={{ color: INK_MID }}>
          <ChevronLeft size={20} />
        </button>
        <div className="ff-mono smallcaps" style={{ color: INK_SOFT }}>Book 3 of 5</div>
        <button className="p-2 -mr-2 rounded-full transition active:scale-95" style={{ color: INK_MID }}>
          <Share2 size={17} strokeWidth={1.7} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide pb-44">
        {/* Hero: cover + title */}
        <div className="px-7 mt-5 flex items-center gap-5">
          <div style={{ flexShrink: 0 }}>
            <BookCover id="diewithzero" w={108} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="smallcaps" style={{ color: ACCENT, marginBottom: 6 }}>Wed · Apr 29 · Today</div>
            <div className="ff-display" style={{ fontSize: 24, lineHeight: 1.05, fontWeight: 600, color: INK, letterSpacing: -0.5 }}>
              Die With Zero
            </div>
            <div className="ff-ui mt-1" style={{ fontSize: 12.5, color: INK_SOFT }}>
              Bill Perkins · 2020
            </div>
            <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
              <span className="ff-ui px-2 py-0.5 rounded-full" style={{ fontSize: 10, background: PAPER_2, color: INK, border: `1px solid ${LINE}`, fontWeight: 500 }}>
                Focus · Legacy &amp; money
              </span>
              <span className="ff-mono" style={{ fontSize: 10, color: INK_SOFT, letterSpacing: 1 }}>
                · 16 MIN
              </span>
            </div>
          </div>
        </div>

        {/* Headline */}
        <div className="px-7 mt-6">
          <div className="smallcaps mb-2" style={{ color: INK_SOFT }}>The one-sentence argument</div>
          <div className="ff-display" style={{ fontSize: 19, lineHeight: 1.42, fontWeight: 500, color: INK, letterSpacing: -0.2 }}>
            "{FEATURED.oneSentence}"
          </div>
        </div>

        {/* Why now (personalized) */}
        <div className="mx-7 mt-6 p-5 rounded-2xl" style={{ background: HIGHLIGHT, border: `1px solid #EAD9A8` }}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={13} strokeWidth={2} style={{ color: ACCENT_DEEP }} />
            <div className="smallcaps" style={{ color: ACCENT_DEEP }}>Why this lands for you · right now</div>
          </div>
          <p className="ff-display" style={{ fontSize: 14.5, lineHeight: 1.55, color: INK }}>
            {FEATURED.whyNow}
          </p>
          <div className="mt-3 pt-3 ff-ui flex items-center gap-2 flex-wrap" style={{ borderTop: `1px solid #EAD9A8`, fontSize: 11, color: INK_MID }}>
            <span className="smallcaps" style={{ color: ACCENT_DEEP, letterSpacing: 1.4 }}>Tuned to</span>
            <span className="px-2 py-0.5 rounded-full" style={{ background: PAPER_3, border: `1px solid ${LINE}`, fontWeight: 500 }}>Type 3w4</span>
            <span className="px-2 py-0.5 rounded-full" style={{ background: PAPER_3, border: `1px solid ${LINE}`, fontWeight: 500 }}>Legacy &amp; money</span>
            <span className="px-2 py-0.5 rounded-full" style={{ background: PAPER_3, border: `1px solid ${LINE}`, fontWeight: 500 }}>Founder · 41 · two kids</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-7 px-7">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className="px-3.5 py-2 rounded-full ff-ui transition flex-shrink-0 active:scale-95" style={{ fontSize: 12, background: tab === t.id ? INK : 'transparent', color: tab === t.id ? PAPER : INK_MID, border: `1px solid ${tab === t.id ? INK : LINE}`, fontWeight: tab === t.id ? 500 : 400 }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="px-7 mt-5">
          {tab === 'argument' && (
            <div className="ff-display" style={{ fontSize: 15, lineHeight: 1.6, color: INK }}>
              <p>{FEATURED.sixty}</p>
              <div className="mt-5 p-4 rounded-xl" style={{ background: PAPER_2, border: `1px solid ${LINE}` }}>
                <div className="smallcaps mb-2" style={{ color: INK_SOFT }}>Contrarian take</div>
                <p style={{ fontSize: 13.5, lineHeight: 1.55, color: INK_MID }}>{FEATURED.contrarian}</p>
              </div>
            </div>
          )}

          {tab === 'ideas' && (
            <div className="space-y-5">
              {FEATURED.bigIdeas.map((idea, i) => (
                <div key={i}>
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <span className="ff-mono" style={{ fontSize: 11, color: ACCENT, letterSpacing: 1 }}>0{i + 1}</span>
                    <div className="ff-display" style={{ fontSize: 17, fontWeight: 600, color: INK, letterSpacing: -0.2 }}>{idea.h}</div>
                  </div>
                  <p className="ff-display" style={{ fontSize: 14, lineHeight: 1.55, color: INK_MID }}>{idea.b}</p>
                </div>
              ))}
            </div>
          )}

          {tab === 'frameworks' && (
            <div className="space-y-3">
              <div className="smallcaps" style={{ color: INK_SOFT, marginBottom: 8 }}>The frameworks you'll actually use</div>
              {FEATURED.frameworks.map((f, i) => (
                <div key={i} className="flex gap-3 p-3.5 rounded-xl" style={{ background: PAPER_2, border: `1px solid ${LINE}` }}>
                  <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ff-mono" style={{ background: ACCENT, color: '#FFFBEB', fontSize: 11, fontWeight: 600 }}>
                    {i + 1}
                  </div>
                  <p className="ff-display flex-1" style={{ fontSize: 14, lineHeight: 1.5, color: INK }}>{f}</p>
                </div>
              ))}
            </div>
          )}

          {tab === 'stories' && (
            <div className="space-y-5">
              {FEATURED.stories.map((s, i) => (
                <div key={i}>
                  <div className="ff-display" style={{ fontSize: 16, fontWeight: 600, color: INK, letterSpacing: -0.2, marginBottom: 6 }}>{s.h}</div>
                  <p className="ff-display" style={{ fontSize: 14, lineHeight: 1.55, color: INK_MID }}>{s.b}</p>
                </div>
              ))}
            </div>
          )}

          {tab === 'takeaways' && (
            <div className="space-y-4">
              <div className="smallcaps" style={{ color: INK_SOFT }}>Direct applications · for Mike</div>
              {FEATURED.takeaways.map((tk, i) => (
                <div key={i} className="border-l-2 pl-3.5 py-1" style={{ borderColor: ACCENT }}>
                  <div className="ff-display" style={{ fontSize: 14.5, fontWeight: 600, color: INK, lineHeight: 1.35, marginBottom: 4 }}>
                    {tk.t}
                  </div>
                  <div className="ff-ui" style={{ fontSize: 12.5, lineHeight: 1.5, color: INK_MID }}>
                    <span style={{ color: ACCENT, fontWeight: 600 }}>Apply:</span> {tk.a}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'questions' && (
            <div className="space-y-4">
              <div className="smallcaps" style={{ color: INK_SOFT }}>Questions to sit with</div>
              {FEATURED.questions.map((q, i) => (
                <div key={i} className="flex gap-3">
                  <span className="ff-mono flex-shrink-0 mt-1" style={{ color: ACCENT, fontSize: 11 }}>Q{i + 1}</span>
                  <p className="ff-display flex-1 leading-[1.5]" style={{ fontSize: 14.5, color: INK, fontWeight: 400 }}>{q}</p>
                </div>
              ))}
              <p className="ff-ui italic text-center mt-7 leading-[1.5]" style={{ color: INK_SOFT, fontSize: 12 }}>
                Don't strategize answers.<br/>The Three in you will want to.<br/>This is about seeing, not solving.
              </p>
            </div>
          )}
        </div>

        {/* Brand sign-off */}
        <div className="mt-12 flex flex-col items-center gap-2.5">
          <StackMark size={20} color={INK_SOFT} />
          <div className="ff-display italic text-center" style={{ color: INK_SOFT, fontSize: 10.5, lineHeight: 1.55 }}>
            Briefed for Mike · Type 3w4<br/>
            Paired to your focus on Legacy &amp; money<br/>
            Week 4 of 52 · Stack
          </div>
        </div>
      </div>

      {/* Audio bar (sticky above action bar) */}
      <div className="absolute left-3 right-3 px-4 py-3 rounded-2xl flex items-center gap-3" style={{ bottom: 86, background: INK, color: '#FFFBEB' }}>
        <button onClick={() => setPlaying(!playing)} className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-90" style={{ background: ACCENT }}>
          {playing ? <Pause size={16} fill="#FFFBEB" strokeWidth={0} /> : <Play size={16} fill="#FFFBEB" strokeWidth={0} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <div className="ff-mono" style={{ fontSize: 10, opacity: 0.7, letterSpacing: 0.8 }}>{fmt(currentSec)} / {fmt(totalSec)}</div>
            <button onClick={cycleSpeed} className="ff-mono" style={{ fontSize: 10, opacity: 0.7, letterSpacing: 0.8 }}>{speed}×</button>
          </div>
          <div className="flex items-center gap-[2px]">
            {Array.from({ length: 36 }).map((_, i) => {
              const r = (i / 36) * 100;
              const active = r < progress;
              const heights = [3, 5, 8, 4, 7, 11, 6, 9, 4, 8, 5, 10, 7, 5, 9, 3, 6, 9, 4, 8, 11, 5, 7, 4, 9, 6, 10, 5, 8, 3, 7, 5, 9, 6, 4, 8];
              return <div key={i} style={{ width: 2.5, height: heights[i], borderRadius: 1, background: active ? ACCENT_LITE : 'rgba(255,251,235,0.18)' }} />;
            })}
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="absolute bottom-0 left-0 right-0 px-5 pb-6 pt-3" style={{ background: `linear-gradient(to top, ${PAPER} 65%, transparent)` }}>
        <div className="flex items-center gap-2 p-1.5 rounded-full" style={{ background: INK }}>
          <button className="flex-1 py-2.5 rounded-full ff-ui text-[12px] font-medium flex items-center justify-center gap-1.5 transition active:scale-95" style={{ color: '#FFFBEB' }}>
            <Highlighter size={13} strokeWidth={1.75} /> Highlight
          </button>
          <button className="flex-1 py-2.5 rounded-full ff-ui text-[12px] font-medium flex items-center justify-center gap-1.5 transition active:scale-95" style={{ color: '#FFFBEB' }}>
            <Bookmark size={13} strokeWidth={1.75} /> Journal
          </button>
          <button className="flex-1 py-2.5 rounded-full ff-ui text-[12px] font-medium flex items-center justify-center gap-1.5 transition active:scale-95" style={{ background: ACCENT, color: '#FFFBEB' }}>
            <Check size={13} strokeWidth={2.25} /> Done
          </button>
        </div>
      </div>

      <HomeIndicator />
    </div>
  );
}

// ============================================================
// MAIN
// ============================================================
export default function StackPrototype() {
  const [screen, setScreen] = useState(0);
  const screens = [
    { name: 'Welcome', component: WelcomeScreen },
    { name: 'Onboarding', component: OnboardingScreen },
    { name: 'Focus', component: FocusAreasScreen },
    { name: 'This week', component: WeeklyPlanScreen },
    { name: "Today's brief", component: BookDetailScreen },
  ];
  const Current = screens[screen].component;

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-start py-6 px-4 ff-ui" style={{ background: `linear-gradient(180deg, ${FRAME_TOP} 0%, ${FRAME_BOT} 100%)` }}>
      <FontStyles />

      <div className="flex-shrink-0 mb-8" style={{ transformOrigin: 'top center', transform: 'scale(min(1, calc(100vw / 440)))' }}>
        <PhoneFrame>
          <Current
            onNext={() => setScreen(Math.min(screens.length - 1, screen + 1))}
            onBack={() => setScreen(Math.max(0, screen - 1))}
          />
        </PhoneFrame>
      </div>

      <div className="w-full max-w-[680px] mt-2">
        <div className="flex items-center justify-center gap-1 flex-wrap">
          {screens.map((s, i) => (
            <button
              key={i}
              onClick={() => setScreen(i)}
              className="px-3.5 py-2 rounded-full ff-ui text-[11px] transition-all active:scale-95"
              style={{
                background: screen === i ? PAPER_3 : 'rgba(31,26,20,0.06)',
                color: screen === i ? INK : 'rgba(31,26,20,0.55)',
                border: screen === i ? `1px solid ${ACCENT}` : '1px solid rgba(31,26,20,0.10)',
                fontWeight: screen === i ? 600 : 400,
              }}
            >
              <span className="ff-mono mr-1.5 opacity-50">{String(i + 1).padStart(2, '0')}</span>
              {s.name}
            </button>
          ))}
        </div>
        <div className="text-center mt-5 ff-ui text-[11px]" style={{ color: 'rgba(31,26,20,0.45)' }}>
          Stack — Five books a week, paired to your five focuses. Tap inside the phone or use the chips above.
        </div>
      </div>
    </div>
  );
}
